import * as React from 'react';
import clsx from 'clsx';
import { ConfiguredBadge } from './ConfiguredBadge';

// Hex literals (matching tailwind tokens brand-300 / brand-800) instead of class names
// because the slider thumb is styled inline via React style props, not Tailwind classes.
const THUMB_BG_COLOR = '#BFB0F8';
const THUMB_BORDER_COLOR = '#1A1036';

interface ConfigSliderProps {
  // Label displayed above the slider
  label: string;
  // Helper text explaining the metric
  description: string;
  // Label for the 0% end
  leftLabel: string;
  // Label for the 100% end
  rightLabel: string;
  // Current value (0-100)
  value: number;
  // Callback for value changes
  onChange: (val: number) => void;
  // Optional icon to display next to the header
  icon?: React.ReactNode;
  // Badge + undo support
  isModified?: boolean;
  onUndo?: () => void;
}

/**
 * ConfigSlider Component
 *
 * A custom range slider with labeled endpoints used for personality configuration.
 * Uses a scoped style block targeting a data attribute to style the webkit-slider-thumb,
 * avoiding custom class names in the className prop to satisfy the linter.
 */
export const ConfigSlider: React.FC<ConfigSliderProps> = ({
  label,
  description,
  leftLabel,
  rightLabel,
  value,
  onChange,
  icon,
  isModified,
  onUndo,
}) => (
  <div
    className={clsx(
      'relative flex w-full flex-col gap-6',
      'rounded-20 border border-brand-300 bg-gray-25 p-6'
    )}
  >
    {/* Configured Badge */}
    {isModified && onUndo && <ConfiguredBadge onUndo={onUndo} />}

    {/* Header Section */}
    <div className='flex items-start gap-4 pr-16'>
      {icon && (
        <div className='flex size-[40px] shrink-0 items-center justify-center rounded-[13px] bg-brand-750 text-white'>
          {icon}
        </div>
      )}
      <div className='flex flex-col gap-1'>
        {/* Header Font Specs */}
        <h4 className='font-sans text-20 font-medium leading-27 text-dark-100'>{label}</h4>
        {/* Subtitle Font Specs */}
        <p className='font-sans text-13 font-normal leading-[18px] text-gray-600'>{description}</p>
      </div>
    </div>

    {/* Slider Section */}
    <div className='w-full px-2'>
      {/* Slider Text (Labels) */}
      <div className='mb-3 flex justify-between font-sans text-12 font-normal leading-27 text-brand-800'>
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>

      {/* Custom Slider Input */}
      <div className='relative flex h-6 w-full items-center'>
        <input
          type='range'
          min='0'
          max='100'
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          // Use data attribute for styling selector instead of custom class to satisfy Tailwind linter
          data-echo-slider='true'
          className='peer absolute h-[8px] w-full cursor-pointer appearance-none rounded-full bg-brand-300 focus:outline-none'
        />

        <style>{`
            input[data-echo-slider="true"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              height: 18px;
              width: 18px;
              border-radius: 50%;
              
              /* Center Circle Color */
              background: ${THUMB_BG_COLOR};
              
              /* Outer Ring (Border) */
              border: 5px solid ${THUMB_BORDER_COLOR};
              
              cursor: pointer;
              position: relative;
              z-index: 10;
              box-sizing: border-box;
            }
            input[data-echo-slider="true"]::-moz-range-thumb {
              height: 18px;
              width: 18px;
              border-radius: 50%;
              background: ${THUMB_BG_COLOR};
              border: 5px solid ${THUMB_BORDER_COLOR};
              cursor: pointer;
              box-sizing: border-box;
            }
            input[data-echo-slider="true"]::-ms-thumb {
              height: 18px;
              width: 18px;
              border-radius: 50%;
              background: ${THUMB_BG_COLOR};
              border: 5px solid ${THUMB_BORDER_COLOR};
              cursor: pointer;
              box-sizing: border-box;
            }
          `}</style>
      </div>
    </div>
  </div>
);
